import { createContext, useContext, useState } from "react";
import { ESignSignerSpec } from "@newLibs/ICFAndSign.lib";
import { DocumentSignerAttrTarget, DocumentSignerAttrType } from "@maven-rest/docs/DocumentSign.dto";
import { v1 as uuidv1, v4 as uuidv4 } from "uuid";

export enum SignerType {
  Signee = "SIGNEE",
  Investigator = "INVESTIGATOR",
  Agent = "AGENT",
}

export const agentIndexGroupUuid = uuidv4();
export const agentIndexAttrUuid = uuidv1();

export const agentIndexAttr = {
  target: DocumentSignerAttrTarget.Signer,
  attr: DocumentSignerAttrType.AgentIndex,
  value: String(new Date().getTime()),
  groupUuid: agentIndexGroupUuid,
  attrUuid: agentIndexAttrUuid,
  pageIndex: null,
  coordX: null,
  coordY: null,
};

export type EConsentSignerSpec = ESignSignerSpec & { type: SignerType };

interface EConsentContextProps {
  signee: EConsentSignerSpec;
  setSignee: React.Dispatch<React.SetStateAction<EConsentSignerSpec>>;
  investigator: EConsentSignerSpec;
  setInvestigator: React.Dispatch<React.SetStateAction<EConsentSignerSpec>>;
}

const EConsentContext = createContext<EConsentContextProps>({
  signee: {
    uuid: "",
    signerIndex: 1,
    type: SignerType.Signee,
    name: "",
    email: "",
    title: "",
    company: "",
    attrs: [],
  },
  setSignee: () => {},
  investigator: {
    uuid: "",
    signerIndex: 2,
    type: SignerType.Investigator,
    name: "",
    email: "",
    title: "",
    company: "",
    attrs: [],
  },
  setInvestigator: () => {},
});

function EConsentProvider({ children }: { children: React.ReactNode }) {
  const [signee, setSignee] = useState<EConsentSignerSpec>({
    uuid: uuidv4(),
    signerIndex: 1,
    type: SignerType.Signee,
    name: "",
    email: "",
    title: "",
    company: "",
    attrs: [agentIndexAttr],
  });

  const [investigator, setInvestigator] = useState<EConsentSignerSpec>({
    uuid: uuidv4(),
    signerIndex: 2,
    type: SignerType.Investigator,
    name: "",
    email: "",
    title: "",
    company: "",
    attrs: [],
  });

  return (
    <EConsentContext.Provider
      value={{
        signee,
        setSignee,
        investigator,
        setInvestigator,
      }}
    >
      {children}
    </EConsentContext.Provider>
  );
}

function useEConsentContext() {
  return useContext(EConsentContext);
}

export { EConsentContext, EConsentProvider, useEConsentContext };
